import {
  ALL_MESSAGES_COLUMNS,
  COMPLETE_EXPORT_COLUMNS,
  MEMBER_CARE_INITIATED_COLUMNS,
  MEMBER_REQUEST_RESPONSE_DATE_COLUMN,
  MEMBER_REQUEST_TURNAROUND_COLUMN,
  buildCompleteExportFileName,
  businessDayTurnaround,
  turnaroundFormula,
} from "./completeExport";

const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DATE_FORMAT = "mm/dd/yyyy";
const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD4F1F4" } };
const HEADER_ROW = 3;
const FIRST_DATA_ROW = 4;
const pad = (value) => String(value).padStart(2, "0");

export const COMPLETE_SHEET_DESCRIPTIONS = {
  "Member Requests": "One row per member request cycle. Answered requests are included when the first Member Care reply is in the selected date range. Unanswered requests from completed threads are included when the conversation completion date is in range and are labeled No Member Care response. Multiple messages from either side are numbered in the message cells.",
  "Member Care Initiated": "Member Care messages sent while no member request was waiting, such as outreach before the member writes. Included when the message date is in the selected date range.",
  "All Messages": "Every message whose own date is in the selected date range, including messages that also appear on the other sheets. One row per message. Messages sharing a thread number belong to the same conversation and are listed together, oldest message first. Threads are ordered by their earliest included message.",
};
const formatRunDate = (date) => `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;

const loadExcelJs = () => import("exceljs/dist/exceljs.min.js").then((module) => module.default || module);

// ExcelJS stores dates as UTC. Writing the local calendar day as UTC keeps the displayed date.
const toExcelDate = (value) => new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));

const toCellValue = (value) => {
  if (value instanceof Date) return toExcelDate(value);
  return value === "" ? null : value;
};

const applyFormat = (worksheet, columnNumbers, numFmt) => {
  columnNumbers.forEach((columnNumber) => {
    worksheet.getColumn(columnNumber).eachCell({ includeEmpty: false }, (cell, rowNumber) => {
      if (rowNumber > 1) cell.numFmt = numFmt;
    });
  });
};

const addDataSheet = (workbook, {
  name,
  columns,
  rows,
  description,
  dateColumns = [],
  wrapTextColumns = [],
  turnaroundFormulaColumn = null,
}, asOf) => {
  const worksheet = workbook.addWorksheet(name, {
    views: [{ state: "frozen", ySplit: HEADER_ROW, topLeftCell: "A4", activeCell: "A4" }],
  });
  worksheet.columns = columns.map((column) => ({ header: column.header, width: column.width }));

  rows.forEach((row) => {
    worksheet.addRow(row.map((value) => toCellValue(value)));
  });
  applyFormat(worksheet, dateColumns, DATE_FORMAT);
  wrapTextColumns.forEach((columnNumber) => {
    worksheet.getColumn(columnNumber).alignment = { wrapText: true, vertical: "top" };
  });

  worksheet.spliceRows(1, 0, [`Run date: ${formatRunDate(asOf)}`], [description]);
  worksheet.mergeCells("A1:B1");
  worksheet.mergeCells(2, 1, 2, columns.length);
  worksheet.getCell("D1").value = `Total rows: ${rows.length}`;
  worksheet.getRow(1).font = { bold: true };
  const descriptionCell = worksheet.getCell("A2");
  descriptionCell.alignment = { wrapText: true, vertical: "center" };
  descriptionCell.font = { bold: false };
  worksheet.getRow(2).height = 36;
  const header = worksheet.getRow(HEADER_ROW);
  header.font = { bold: true };
  for (let column = 1; column <= columns.length; column += 1) {
    header.getCell(column).fill = HEADER_FILL;
  }
  worksheet.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: Math.max(worksheet.rowCount, HEADER_ROW), column: columns.length },
  };
  if (turnaroundFormulaColumn) applyTurnaroundFormulas(worksheet, turnaroundFormulaColumn);

  return worksheet;
};

// Excel date cells are UTC midnight for the displayed calendar day.
const calendarTimestamp = (excelDate) => new Date(
  excelDate.getUTCFullYear(),
  excelDate.getUTCMonth(),
  excelDate.getUTCDate(),
).getTime();

const applyTurnaroundFormulas = (worksheet, columnNumber) => {
  for (let rowNumber = FIRST_DATA_ROW; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const row = worksheet.getRow(rowNumber);
    const initialDate = row.getCell(1).value;
    const responseDate = row.getCell(MEMBER_REQUEST_RESPONSE_DATE_COLUMN).value;
    if (!(initialDate instanceof Date) || !(responseDate instanceof Date)) continue;
    row.getCell(columnNumber).value = {
      formula: turnaroundFormula(rowNumber),
      result: businessDayTurnaround(calendarTimestamp(initialDate), calendarTimestamp(responseDate)),
    };
    row.getCell(columnNumber).numFmt = "0";
  }
};

export const buildCompleteWorkbook = async (exportRows, asOf = new Date()) => {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();

  addDataSheet(workbook, {
    name: "Member Requests",
    columns: COMPLETE_EXPORT_COLUMNS,
    rows: exportRows.memberRequests || [],
    description: COMPLETE_SHEET_DESCRIPTIONS["Member Requests"],
    dateColumns: [1, 2, 3, MEMBER_REQUEST_RESPONSE_DATE_COLUMN],
    wrapTextColumns: [6, 11, 15, 16],
    turnaroundFormulaColumn: MEMBER_REQUEST_TURNAROUND_COLUMN,
  }, asOf);
  addDataSheet(workbook, {
    name: "Member Care Initiated",
    columns: MEMBER_CARE_INITIATED_COLUMNS,
    rows: exportRows.memberCareInitiated || [],
    description: COMPLETE_SHEET_DESCRIPTIONS["Member Care Initiated"],
    dateColumns: [1],
    wrapTextColumns: [5, 10, 11],
  }, asOf);
  addDataSheet(workbook, {
    name: "All Messages",
    columns: ALL_MESSAGES_COLUMNS,
    rows: exportRows.allMessages || [],
    description: COMPLETE_SHEET_DESCRIPTIONS["All Messages"],
    dateColumns: [2],
    wrapTextColumns: [7, 12, 13],
  }, asOf);

  return workbook;
};

const downloadFile = (buffer, fileName) => {
  const url = URL.createObjectURL(new Blob([buffer], { type: XLSX_MIME_TYPE }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const exportCompleteToExcel = async (exportRows, date = new Date()) => {
  const fileName = buildCompleteExportFileName(date);
  const workbook = await buildCompleteWorkbook(exportRows, date);
  downloadFile(await workbook.xlsx.writeBuffer(), fileName);
  return fileName;
};
